import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  name: string;
  tipoPerfilEnum: number;
  tipoPerfil: string;
  grupos: {
    nome: string;
    roles: string[];
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly baseUrl = `${environment.apiurl}/api/Auth`;

  private readonly accessTokenKey = 'sp_access_token';
  private readonly refreshTokenKey = 'sp_refresh_token';
  private readonly expiresAtKey = 'sp_expires_at';
  private readonly nameKey = 'sp_user_name';
  private readonly gruposKey = 'sp_user_grupos';
  private readonly tipoPerfilEnumKey = 'sp_user_tipo_perfil_enum';

  readonly isAuthenticated = new BehaviorSubject<boolean>(
    this.hasValidStoredSession(),
  );

  /** POST /api/Auth/login — credenciais reais do utilizador */
  login(username: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, { username, password })
      .pipe(tap((res) => this.handleAuthResponse(res)));
  }

  /**
   * POST /api/Auth/loginFirst — troca da palavra-passe temporária no primeiro acesso.
   * O Swagger não documenta o corpo de resposta: só autenticamos automaticamente
   * se a resposta trouxer um accessToken.
   */
  loginFirst(
    username: string,
    passwordTemporaria: string,
    novaPassword: string,
  ): Observable<Partial<LoginResponse>> {
    return this.http
      .post<Partial<LoginResponse>>(`${this.baseUrl}/loginFirst`, {
        username,
        passwordTemporaria,
        novaPassword,
      })
      .pipe(
        tap((res) => {
          if (res?.accessToken) {
            this.handleAuthResponse(res as LoginResponse);
          }
        }),
      );
  }

  /** PUT /api/Auth/ResetarSenha — ação administrativa, requer sessão autenticada. */
  resetarSenha(payload: {
    userName: string;
    email: string;
    novaPassword: string;
  }): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/ResetarSenha`, payload);
  }

  private handleAuthResponse(res: LoginResponse): void {
    localStorage.setItem(this.accessTokenKey, res.accessToken);
    localStorage.setItem(this.refreshTokenKey, res.refreshToken ?? '');
    localStorage.setItem(
      this.expiresAtKey,
      String(this.resolveExpiresAt(res)),
    );
    localStorage.setItem(this.nameKey, res.name ?? '');
    localStorage.setItem(this.gruposKey, JSON.stringify(res.grupos ?? []));
    localStorage.setItem(
      this.tipoPerfilEnumKey,
      String(res.tipoPerfilEnum ?? ''),
    );
    this.isAuthenticated.next(true);
  }

  /**
   * O `exp` embutido no JWT é a fonte de verdade (segundos desde epoch, definidos
   * pelo servidor). O `expiresIn` do corpo da resposta serve só de fallback caso
   * o token não seja um JWT decodificável — evita ficar dependente da unidade/
   * nome exato desse campo, que já causou expiração prematura (logout no reload).
   */
  private resolveExpiresAt(res: LoginResponse): number {
    const exp = this.decodeJwtExp(res.accessToken);
    if (exp) return exp * 1000;
    return Date.now() + (res.expiresIn ?? 0) * 1000;
  }

  private decodeJwtExp(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        '=',
      );
      const json = atob(padded);
      const { exp } = JSON.parse(json);
      return typeof exp === 'number' ? exp : null;
    } catch {
      return null;
    }
  }

  logout(): void {
    this.clearStoredSession();
    this.isAuthenticated.next(false);
    this.router.navigate(['/login']);
  }

  private clearStoredSession(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.expiresAtKey);
    localStorage.removeItem(this.nameKey);
    localStorage.removeItem(this.gruposKey);
    localStorage.removeItem(this.tipoPerfilEnumKey);
  }

  getAccessToken(): string | null {
    return this.restoreSession()
      ? localStorage.getItem(this.accessTokenKey)
      : null;
  }

  getUserName(): string {
    return localStorage.getItem(this.nameKey) || '';
  }

  getUser(): { nome?: string } | null {
    const nome = this.getUserName();
    return nome ? { nome } : null;
  }

  getRoles(): string[] {
    try {
      const raw = localStorage.getItem(this.gruposKey);
      if (!raw) return [];
      const grupos = JSON.parse(raw) as Array<{ roles?: string[] }>;
      if (!Array.isArray(grupos)) return [];
      const roles = new Set<string>();
      for (const grupo of grupos) {
        if (Array.isArray(grupo?.roles)) {
          for (const role of grupo.roles) {
            if (typeof role === 'string' && role.trim()) {
              roles.add(role.trim());
            }
          }
        }
      }
      return Array.from(roles);
    } catch {
      return [];
    }
  }

  hasRole(role: string): boolean {
    if (!role) return true;
    const roles = this.getRoles();
    if (!roles.length) return true;
    return roles.includes(role);
  }

  getGrupos(): string[] {
    try {
      const grupos = JSON.parse(localStorage.getItem(this.gruposKey) ?? '[]');
      if (!Array.isArray(grupos)) return [];
      return grupos
        .map((grupo: unknown) => {
          if (typeof grupo === 'string') return grupo;
          const item = grupo as { nome?: string } | null;
          return item?.nome ?? '';
        })
        .filter((nome: string) => nome.length > 0);
    } catch {
      return [];
    }
  }

  isAdmin(): boolean {
    return Number(localStorage.getItem(this.tipoPerfilEnumKey)) === 3;
  }

  isLoggedIn(): boolean {
    return this.restoreSession();
  }

  /**
   * Restaura a autenticação guardada após reload/HMR. O `exp` do JWT é a fonte
   * principal e também recupera sessões antigas que não tenham `sp_expires_at`.
   */
  restoreSession(): boolean {
    const valid = this.hasValidStoredSession();
    this.isAuthenticated.next(valid);
    return valid;
  }

  private hasValidStoredSession(): boolean {
    const token = localStorage.getItem(this.accessTokenKey);
    if (!token) return false;

    const jwtExp = this.decodeJwtExp(token);
    const storedExpiresAt = Number(localStorage.getItem(this.expiresAtKey));
    const expiresAt = jwtExp ? jwtExp * 1000 : storedExpiresAt;

    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      this.clearStoredSession();
      return false;
    }

    if (jwtExp && storedExpiresAt !== expiresAt) {
      localStorage.setItem(this.expiresAtKey, String(expiresAt));
    }

    return true;
  }
}
